<template>
  <div class="air__utils__content">
    <div class="air__utils__heading">
      <div class="row align-items-center">
        <div class="col-md-6 col-12 mb-2 mb-md-0">
          <h5 class="mb-0 font-weight-bold">
            <i class="fe fe-video mr-2 text-primary"></i> Zoom Online Class Meetings
          </h5>
          <small class="text-muted">Manage recurring Zoom meeting links assigned to classes, sections, and subjects</small>
        </div>
        <div class="col-md-6 col-12 text-md-right">
          <a-button type="primary" size="large" icon="plus" @click="openCreateModal">
            Create Zoom Class
          </a-button>
        </div>
      </div>
    </div>

    <!-- Filter Card -->
    <a-card class="mb-4" :bordered="false">
      <div class="row">
        <!-- Session Filter -->
        <div class="col-md-3 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Academic Session</label>
          <a-select v-model="selectedSession" style="width: 100%" @change="handleSessionChange" placeholder="Select Session">
            <a-select-option v-for="s in schoolSessions" :key="s.id" :value="s.id">
              {{ s.name }} <span v-if="s.is_current" class="text-primary font-weight-bold">(Current)</span>
            </a-select-option>
          </a-select>
        </div>

        <!-- Class Filter -->
        <div class="col-md-3 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Class</label>
          <a-select v-model="filterClass" style="width: 100%" @change="handleFilterClassChange" placeholder="All Classes" :loading="classSpinning" allowClear>
            <a-select-option :value="0">All Classes</a-select-option>
            <a-select-option v-for="c in schoolClasses" :key="c.id" :value="c.id">
              {{ c.name }}
            </a-select-option>
          </a-select>
        </div>

        <!-- Section Filter -->
        <div class="col-md-3 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Section</label>
          <a-select v-model="filterSection" style="width: 100%" @change="fetchMeetings" placeholder="All Sections" :disabled="!filterClass || filterClass === 0" allowClear>
            <a-select-option :value="0">All Sections</a-select-option>
            <a-select-option v-for="sec in filterSections" :key="sec.id" :value="sec.id">
              {{ sec.name }}
            </a-select-option>
          </a-select>
        </div>

        <!-- Teacher Filter -->
        <div class="col-md-3 col-sm-6 col-12 mb-3">
          <label class="font-weight-bold text-dark mb-1">Teacher</label>
          <a-select v-model="filterTeacher" style="width: 100%" @change="fetchMeetings" placeholder="All Teachers" allowClear showSearch optionFilterProp="children">
            <a-select-option :value="0">All Teachers</a-select-option>
            <a-select-option v-for="t in teachers" :key="t.id" :value="t.id">
              {{ t.first_name }} {{ t.last_name }}
            </a-select-option>
          </a-select>
        </div>
      </div>
    </a-card>

    <!-- Meetings Table -->
    <a-card :bordered="false" class="shadow-sm">
      <div class="table-responsive">
        <a-table
          :columns="columns"
          :dataSource="meetings"
          :rowKey="record => record.id"
          :loading="tableLoading"
          :pagination="{ pageSize: 15, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} meetings` }"
        >
          <!-- Class & Section -->
          <template slot="classSection" slot-scope="text, record">
            <span class="font-weight-bold text-dark">
              {{ record.school_class ? record.school_class.name : '-' }}
            </span>
            <span v-if="record.section" class="badge badge-light border ml-1">
              Sec {{ record.section.name }}
            </span>
            <span v-else class="badge badge-info ml-1">
              All Sections
            </span>
            <div v-if="record.meeting_nickname" class="text-muted font-size-12 mt-1">
              {{ record.meeting_nickname }}
            </div>
          </template>

          <!-- Subject -->
          <template slot="subject" slot-scope="text, record">
            <span class="badge badge-primary font-size-12 px-2 py-1">
              {{ record.subject ? record.subject.name : '-' }}
            </span>
          </template>

          <!-- Teacher -->
          <template slot="teacher" slot-scope="text, record">
            <div v-if="record.teacher" class="d-flex align-items-center">
              <a-avatar size="small" icon="user" class="mr-2 bg-secondary" />
              <span>{{ record.teacher.first_name }} {{ record.teacher.last_name }}</span>
            </div>
            <span v-else class="text-muted">-</span>
          </template>

          <!-- Meeting ID & Passcode -->
          <template slot="meetingInfo" slot-scope="text, record">
            <div class="font-weight-bold text-monospace font-size-13">
              {{ formatMeetingId(record.zoom_meeting_id) }}
            </div>
            <div v-if="record.zoom_meeting_password" class="text-muted font-size-12">
              Passcode: <span class="font-weight-bold">{{ record.zoom_meeting_password }}</span>
            </div>
          </template>

          <!-- Status -->
          <template slot="status" slot-scope="text, record">
            <a-switch
              :checked="record.is_active"
              checkedChildren="Active"
              unCheckedChildren="Inactive"
              @change="(checked) => toggleStatus(record, checked)"
            />
          </template>

          <!-- Host & Actions -->
          <template slot="actions" slot-scope="text, record">
            <div class="d-flex align-items-center justify-content-end">
              <!-- Start Meeting (Host URL) -->
              <a-tooltip title="Open Host Start URL in Zoom">
                <a
                  v-if="record.zoom_host_start_url"
                  :href="record.zoom_host_start_url"
                  target="_blank"
                  class="btn btn-sm btn-outline-primary mr-2"
                >
                  <i class="fe fe-video mr-1"></i> Start Host
                </a>
              </a-tooltip>

              <!-- Attendance Report Link -->
              <a-tooltip title="View Class Attendance Logs">
                <router-link :to="{ path: '/zoom-attendance', query: { class_id: record.class_id, section_id: record.section_id, subject_id: record.subject_id } }" class="btn btn-sm btn-outline-info mr-2">
                  <i class="fe fe-users"></i>
                </router-link>
              </a-tooltip>

              <!-- Edit -->
              <a-tooltip title="Edit Details">
                <a-button size="small" icon="edit" class="mr-2" @click="openEditModal(record)" />
              </a-tooltip>

              <!-- Delete -->
              <a-popconfirm
                title="Are you sure you want to delete this Zoom meeting link?"
                okText="Yes, delete"
                cancelText="Cancel"
                @confirm="deleteMeeting(record.id)"
              >
                <a-button size="small" type="danger" icon="trash" ghost />
              </a-popconfirm>
            </div>
          </template>
        </a-table>
      </div>
    </a-card>

    <!-- Create Zoom Meeting Modal -->
    <a-modal
      v-model="createModalVisible"
      title="Create New Zoom Online Class"
      :width="650"
      :footer="null"
      destroyOnClose
    >
      <a-form :form="createForm" layout="vertical" @submit="handleCreateSubmit">
        <div class="row">
          <!-- Class -->
          <div class="col-md-6 col-12">
            <a-form-item label="Class">
              <a-select
                v-decorator="['class_id', { rules: [{ required: true, message: 'Please select class' }] }]"
                placeholder="Select Class"
                @change="handleModalClassChange"
              >
                <a-select-option v-for="c in schoolClasses" :key="c.id" :value="c.id">
                  {{ c.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>

          <!-- Section -->
          <div class="col-md-6 col-12">
            <a-form-item label="Section" extra="Leave blank to apply to all sections combined">
              <a-select
                v-decorator="['section_id']"
                placeholder="All Sections (Combined)"
                allowClear
              >
                <a-select-option v-for="sec in modalSections" :key="sec.id" :value="sec.id">
                  Section {{ sec.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>

          <!-- Subject -->
          <div class="col-md-6 col-12">
            <a-form-item label="Subject">
              <a-select
                v-decorator="['subject_id', { rules: [{ required: true, message: 'Please select subject' }] }]"
                placeholder="Select Subject"
              >
                <a-select-option v-for="sub in modalSubjects" :key="sub.id" :value="sub.id">
                  {{ sub.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>

          <!-- Teacher -->
          <div class="col-md-6 col-12">
            <a-form-item label="Assigned Teacher">
              <a-select
                v-decorator="['teacher_id', { rules: [{ required: true, message: 'Please select teacher' }] }]"
                placeholder="Select Teacher"
                showSearch
                optionFilterProp="children"
              >
                <a-select-option v-for="t in teachers" :key="t.id" :value="t.id">
                  {{ t.first_name }} {{ t.last_name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>

          <!-- Nickname / Topic -->
          <div class="col-12">
            <a-form-item label="Class Title / Nickname (Optional)" extra="e.g. Physics Class 10-A. If left empty, system auto-generates title.">
              <a-input
                v-decorator="['meeting_nickname']"
                placeholder="e.g. Physics Class 10-A"
              />
            </a-form-item>
          </div>

          <!-- Manual Mode Toggle -->
          <div class="col-12 mb-3">
            <a-checkbox v-model="isManualMode">
              I already have an existing Zoom Meeting ID (Manual Entry)
            </a-checkbox>
          </div>

          <!-- Manual Fields (Conditional) -->
          <template v-if="isManualMode">
            <div class="col-md-6 col-12">
              <a-form-item label="Zoom Meeting ID">
                <a-input
                  v-decorator="['zoom_meeting_id', { rules: [{ required: isManualMode, message: 'Please enter Meeting ID' }] }]"
                  placeholder="e.g. 892 1234 5678"
                />
              </a-form-item>
            </div>
            <div class="col-md-6 col-12">
              <a-form-item label="Zoom Passcode">
                <a-input
                  v-decorator="['zoom_meeting_password']"
                  placeholder="e.g. 123456"
                />
              </a-form-item>
            </div>
            <div class="col-12">
              <a-form-item label="Host Start URL (Optional)">
                <a-input
                  v-decorator="['zoom_host_start_url']"
                  placeholder="https://zoom.us/s/..."
                />
              </a-form-item>
            </div>
          </template>
        </div>

        <div class="mt-4 pt-3 border-top text-right">
          <a-button class="mr-2" @click="createModalVisible = false">Cancel</a-button>
          <a-button type="primary" html-type="submit" :loading="modalSaving">
            <i class="fe fe-check mr-1"></i> Create Meeting
          </a-button>
        </div>
      </a-form>
    </a-modal>

    <!-- Edit Zoom Meeting Modal -->
    <a-modal
      v-model="editModalVisible"
      title="Edit Zoom Meeting Link"
      :width="550"
      :footer="null"
      destroyOnClose
    >
      <a-form :form="editForm" layout="vertical" @submit="handleEditSubmit">
        <a-form-item label="Meeting Nickname / Title">
          <a-input
            v-decorator="['meeting_nickname']"
            placeholder="e.g. Physics Class 10-A"
          />
        </a-form-item>

        <a-form-item label="Zoom Meeting ID">
          <a-input
            v-decorator="['zoom_meeting_id', { rules: [{ required: true, message: 'Please enter Meeting ID' }] }]"
          />
        </a-form-item>

        <a-form-item label="Zoom Passcode">
          <a-input
            v-decorator="['zoom_meeting_password']"
          />
        </a-form-item>

        <a-form-item label="Host Start URL">
          <a-input
            v-decorator="['zoom_host_start_url']"
          />
        </a-form-item>

        <div class="mt-4 pt-3 border-top text-right">
          <a-button class="mr-2" @click="editModalVisible = false">Cancel</a-button>
          <a-button type="primary" html-type="submit" :loading="editSaving">
            Update Meeting
          </a-button>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import find from 'lodash/find'

export default {
  data() {
    return {
      tableLoading: false,
      classSpinning: false,
      selectedSession: null,
      schoolSessions: [],
      schoolClasses: [],
      teachers: [],
      filterClass: 0,
      filterSection: 0,
      filterTeacher: 0,
      filterSections: [],
      meetings: [],

      // Create Modal
      createModalVisible: false,
      modalSaving: false,
      isManualMode: false,
      modalSections: [],
      modalSubjects: [],
      createForm: this.$form.createForm(this),

      // Edit Modal
      editModalVisible: false,
      editSaving: false,
      editingMeetingId: null,
      editForm: this.$form.createForm(this),

      columns: [
        { title: 'Class & Section', scopedSlots: { customRender: 'classSection' }, width: 220 },
        { title: 'Subject', scopedSlots: { customRender: 'subject' }, width: 160 },
        { title: 'Teacher', scopedSlots: { customRender: 'teacher' } },
        { title: 'Zoom Meeting ID', scopedSlots: { customRender: 'meetingInfo' }, width: 200 },
        { title: 'Status', scopedSlots: { customRender: 'status' }, width: 120 },
        { title: 'Actions', scopedSlots: { customRender: 'actions' }, width: 200, className: 'text-right' },
      ],
    }
  },
  mounted() {
    this.fetchSessions()
    this.fetchTeachers()
  },
  methods: {
    formatMeetingId(id) {
      if (!id) return '-'
      const str = String(id).replace(/\s/g, '')
      if (str.length === 10) {
        return str.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')
      } else if (str.length === 11) {
        return str.replace(/(\d{3})(\d{4})(\d{4})/, '$1 $2 $3')
      }
      return str
    },

    fetchSessions() {
      this.$http.get('/api/session').then(({ data }) => {
        this.schoolSessions = data.data || []
        const current = find(this.schoolSessions, ['is_current', 1])
        if (current) {
          this.selectedSession = current.id
        } else if (this.schoolSessions.length > 0) {
          this.selectedSession = this.schoolSessions[0].id
        }
        this.fetchClasses()
        this.fetchMeetings()
      })
    },

    handleSessionChange(val) {
      this.selectedSession = val
      this.fetchClasses()
      this.fetchMeetings()
    },

    fetchClasses() {
      this.classSpinning = true
      this.$http.get('/api/class', { params: { session_id: this.selectedSession } }).then(({ data }) => {
        this.schoolClasses = data.data || []
        this.classSpinning = false
      }).catch(() => {
        this.classSpinning = false
      })
    },

    fetchTeachers() {
      this.$http.get('/api/staff').then(({ data }) => {
        this.teachers = data.data || []
      }).catch(() => {})
    },

    handleFilterClassChange(val) {
      this.filterClass = val
      this.filterSection = 0
      if (val && val !== 0) {
        this.$http.get(`/api/section/class/${val}`).then(({ data }) => {
          this.filterSections = data.data || []
        })
      } else {
        this.filterSections = []
      }
      this.fetchMeetings()
    },

    fetchMeetings() {
      this.tableLoading = true
      const params = {
        session_id: this.selectedSession,
      }
      if (this.filterClass) params.class_id = this.filterClass
      if (this.filterSection) params.section_id = this.filterSection
      if (this.filterTeacher) params.teacher_id = this.filterTeacher

      this.$http.get('/api/zoom/meetings', { params })
        .then(({ data }) => {
          this.meetings = data.data || []
        })
        .catch(err => {
          console.error(err)
          this.$notification.error({
            message: 'Error',
            description: 'Failed to load Zoom meetings.',
          })
        })
        .finally(() => {
          this.tableLoading = false
        })
    },

    // ─── Modal Handlers ─────────────────────────────────────────────

    openCreateModal() {
      this.isManualMode = false
      this.modalSections = []
      this.modalSubjects = []
      this.createForm.resetFields()
      this.createModalVisible = true
    },

    handleModalClassChange(classId) {
      this.createForm.setFieldsValue({ section_id: undefined, subject_id: undefined })
      this.modalSections = []
      this.modalSubjects = []
      if (!classId) return

      // Load sections
      this.$http.get(`/api/section/class/${classId}`).then(({ data }) => {
        this.modalSections = data.data || []
      })

      // Load subjects
      this.$http.get(`/api/subject/class/${classId}`).then(({ data }) => {
        this.modalSubjects = data.data.normal_subjects || data.data || []
      })
    },

    handleCreateSubmit(e) {
      e.preventDefault()
      this.createForm.validateFields((err, values) => {
        if (!err) {
          this.modalSaving = true
          const payload = {
            ...values,
            session_id: this.selectedSession,
          }

          this.$http.post('/api/zoom/meetings', payload)
            .then(({ data }) => {
              this.$notification.success({
                message: 'Success',
                description: data.message || 'Zoom meeting created successfully!',
              })
              this.createModalVisible = false
              this.fetchMeetings()
            })
            .catch(error => {
              console.error(error)
              const msg = error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : 'Failed to create Zoom meeting.'
              this.$notification.error({
                message: 'Error',
                description: msg,
              })
            })
            .finally(() => {
              this.modalSaving = false
            })
        }
      })
    },

    openEditModal(record) {
      this.editingMeetingId = record.id
      this.editModalVisible = true
      this.$nextTick(() => {
        this.editForm.setFieldsValue({
          meeting_nickname: record.meeting_nickname,
          zoom_meeting_id: record.zoom_meeting_id,
          zoom_meeting_password: record.zoom_meeting_password,
          zoom_host_start_url: record.zoom_host_start_url,
        })
      })
    },

    handleEditSubmit(e) {
      e.preventDefault()
      this.editForm.validateFields((err, values) => {
        if (!err && this.editingMeetingId) {
          this.editSaving = true
          this.$http.put(`/api/zoom/meetings/${this.editingMeetingId}`, values)
            .then(({ data }) => {
              this.$notification.success({
                message: 'Success',
                description: data.message || 'Zoom meeting updated successfully!',
              })
              this.editModalVisible = false
              this.fetchMeetings()
            })
            .catch(error => {
              console.error(error)
              this.$notification.error({
                message: 'Error',
                description: 'Failed to update meeting.',
              })
            })
            .finally(() => {
              this.editSaving = false
            })
        }
      })
    },

    toggleStatus(record, checked) {
      this.$http.put(`/api/zoom/meetings/${record.id}`, { is_active: checked })
        .then(() => {
          record.is_active = checked
          this.$notification.success({
            message: 'Status Updated',
            description: `Meeting marked as ${checked ? 'Active' : 'Inactive'}.`,
          })
        })
        .catch(err => {
          console.error(err)
          this.$notification.error({
            message: 'Error',
            description: 'Failed to update meeting status.',
          })
        })
    },

    deleteMeeting(id) {
      this.$http.delete(`/api/zoom/meetings/${id}`)
        .then(({ data }) => {
          this.$notification.success({
            message: 'Deleted',
            description: data.message || 'Zoom meeting deleted.',
          })
          this.fetchMeetings()
        })
        .catch(error => {
          console.error(error)
          this.$notification.error({
            message: 'Error',
            description: 'Failed to delete meeting.',
          })
        })
    },
  },
}
</script>
